import clsx from 'clsx';
import ActivityProgress from 'core/modules/atoms/ActivityProgress/ActivityProgress';
import { Icon, Image, ListSimpleDropdown, OptionsDropdownMenu, Popover, TextHtml, CircularProgress } from 'ui';
import ItemComplexList from 'core/modules/components/general/ItemComplexList';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import useModuleItem from './useModuleItem';

function ModuleItem({
  contentLang,
  modules,
  name,
  guid,
  module_guid,
  visibility,
  parent_guid,
  showOptionsInItems,
  image,
  sessionInfo,
  onChangeModuleVisibility,
  isStudent,
  studentGradebook,
  enableOverlays,
  twoLevelProgram,
  isAccessible,
  isEdelvives,
  isEditorial,
}) {
  const [imageLoaded, setImageLoaded] = useState(image ? false : true);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const {
    t,
    options,
    optionsModules,
    anchorLessons,
    openDropDownLessons,
    progress,
    isCompleted,
    hasCurrentSession,
    allSessionsCompleted,
    sessionDateDatesInfo,
    sessionProgress,
    handleCloseDropdown,
    onClickInOption,
  } = useModuleItem({
    modules,
    parent_guid,
    visibility,
    guid,
    module_guid,
    onChangeModuleVisibility,
    sessionInfo,
    studentGradebook,
    isEdelvives,
    isEditorial,
    twoLevelProgram,
  });

  function renderCenter() {
    return (
      <>
        <div className={`module-item__center`}>
          {sessionInfo[0]?.session_date && !isStudent && !twoLevelProgram && (
            <div className="module-item__progress-circle">
              <CircularProgress label={t('lesson:Progress')} progress={sessionProgress} />
            </div>
          )}

          {image && (
            <div className={`module-item__cover ${imageLoaded ? 'module-item__cover--loaded' : ''}`}>
              <Image src={image} onLoad={handleImageLoad} alt={t('alt:Lesson lessonName cover', { lessonName: name })} />
            </div>
          )}
          {!image && <div className="module-item__cover-empty"></div>}

          <div className="module-item__info">
            <div className="module-item__info-top">
              <TextHtml className={'module-item__name'} text={name} lang={contentLang} />
              {visibility === 0 && (
                <div className="module-item__visibility-container">
                  <Icon name={'visible-off'} color="red" />
                  <span className="module-item__visibility"> {t('course:Only you can see this module')} </span>
                </div>
              )}
            </div>
            {!isAccessible && <span className="module-item__coming-soon">{t('common:Coming soon')}</span>}
            {sessionInfo?.length > 0 && (
              <div className="module-item__session-info">
                {!isStudent && (
                  <span className="module-item__session-info-text">
                    {sessionInfo.length} {t('course:sessions')}
                  </span>
                )}
                {sessionDateDatesInfo && <span className="module-item__session-info-date">{sessionDateDatesInfo}</span>}
              </div>
            )}
          </div>
        </div>
        <Popover
          anchorEl={anchorLessons}
          open={openDropDownLessons}
          handleClose={handleCloseDropdown}
          className="module-element-item__dropdown-lib"
          placement="right-start"
          shouldFocusOnOpen={false}
        >
          <ListSimpleDropdown items={optionsModules} onClick={onClickInOption} preventEvent={true} shouldAutoFocusFirstItem />
        </Popover>
      </>
    );
  }

  function renderRight() {
    return (
      <div className={clsx('module-item__right', { 'module-item__right--mode-edit': !isStudent })}>
        {showOptionsInItems && !enableOverlays && (
          <div className={'module-item__menu'}>
            <OptionsDropdownMenu options={options} />
          </div>
        )}
        {isStudent && progress?.total > 0 && (
          <div className="module-item__student-progress">
            <ActivityProgress completed={progress?.completed || 0} total={progress?.total || 0} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={clsx('module-item', {
        'module-item--no-visibility': visibility === 0,
        'module-item--active': hasCurrentSession,
        'module-item--disabled': allSessionsCompleted,
        'module-item--not-accessible': !isAccessible,
        'module-item--student': isStudent,
        'module-item--completed': isCompleted,
      })}
      id={module_guid}
    >
      <ItemComplexList center={renderCenter()} right={renderRight()} />
    </div>
  );
}

ModuleItem.propTypes = {
  contentLang: PropTypes.string,
  modules: PropTypes.array,
  name: PropTypes.string,
  date: PropTypes.string,
  guid: PropTypes.string,
  module_guid: PropTypes.string,
  visibility: PropTypes.number,
  parent_guid: PropTypes.string,
  sessionInfo: PropTypes.array,
  showOptionsInItems: PropTypes.bool,
  image: PropTypes.string,
  onChangeModuleVisibility: PropTypes.func,
  isStudent: PropTypes.bool,
  studentGradebook: PropTypes.object,
  enableOverlays: PropTypes.bool,
  isAccessible: PropTypes.bool,
  twoLevelProgram: PropTypes.bool,
  isEdelvives: PropTypes.bool,
  isEditorial: PropTypes.bool,
};

ModuleItem.defaultProps = {
  modules: [],
  name: '',
  date: '',
  guid: '',
  module_guid: '',
  visibility: 0,
  parent_guid: '',
  sessionInfo: [],
  showOptionsInItems: false,
  image: '',
  onChangeModuleVisibility: () => {},
  studentGradebook: null,
  isAccessible: true,
  enableOverlays: false,
  twoLevelProgram: false,
  isEdelvives: false,
  isEditorial: false,
};

export default ModuleItem;
